import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cn } from "../../utils/helpers";

export interface RadixMultiSelectOption {
  value: string;
  label: string;
}

export interface RadixMultiSelectProps {
  options: RadixMultiSelectOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  allLabel?: string;
  className?: string;
  placeholder?: string;
  renderDisplayValue?: (selected: string[], options: RadixMultiSelectOption[]) => string;
}

export const RadixMultiSelect: React.FC<RadixMultiSelectProps> = ({
  options,
  selectedValues,
  onSelectionChange,
  allLabel = "All Regions",
  className,
  placeholder = "Select...",
  renderDisplayValue,
}) => {
  const allSelected = selectedValues.length === options.length;

  const handleAllToggle = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(options.map((o) => o.value));
    }
  };

  const handleItemToggle = (value: string) => {
    const isSelected = selectedValues.includes(value);
    const newValues = isSelected
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelectionChange(newValues);
  };

  const displayText = React.useMemo(() => {
    if (renderDisplayValue) return renderDisplayValue(selectedValues, options);
    if (!options.length) return placeholder;
    if (selectedValues.length === 0) return placeholder;
    if (allSelected) return allLabel;
    const first = options.find((o) => o.value === selectedValues[0]);
    const extra = selectedValues.length - 1;
    return extra > 0 ? `${first?.label} +${extra}` : first?.label || placeholder;
  }, [selectedValues, options, allSelected, allLabel, placeholder, renderDisplayValue]);

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          className={cn(
            "inline-flex items-center justify-between rounded border border-white/40 bg-transparent px-3 py-1.5 text-sm text-white cursor-pointer shadow-sm relative select-none self-center",
            "hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30",
            className
          )}
          style={{
            minHeight: '32px',
            height: '32px',
            minWidth: '160px',
            borderColor: 'rgba(255,255,255,0.4)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        >
          <span className="truncate mr-2">{displayText}</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[9999] rounded-md border border-border bg-popover text-popover-foreground shadow-md py-1 min-w-[180px]"
          sideOffset={4}
          align="start"
        >
          {/* All Regions checkbox */}
          <DropdownMenu.CheckboxItem
            checked={allSelected}
            onCheckedChange={handleAllToggle}
            onSelect={(e) => e.preventDefault()}
            className={cn(
              "flex items-center gap-2 cursor-pointer select-none px-3 py-1.5 text-sm text-foreground",
              "hover:bg-accent focus:bg-accent data-[highlighted]:bg-accent outline-none"
            )}
          >
            <span className="flex items-center justify-center w-4 h-4 rounded-sm border border-input">
              {allSelected && (
                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                  <polyline points="4 10 8 15 16 5" />
                </svg>
              )}
            </span>
            <span className="font-medium">{allLabel}</span>
          </DropdownMenu.CheckboxItem>

          <DropdownMenu.Separator className="h-px bg-border my-0.5" />

          {/* Individual options */}
          <div className="overflow-y-auto max-h-48">
            {options.map((opt) => {
              const isChecked = selectedValues.includes(opt.value);
              return (
                <DropdownMenu.CheckboxItem
                  key={opt.value}
                  checked={isChecked}
                  onCheckedChange={() => handleItemToggle(opt.value)}
                  onSelect={(e) => e.preventDefault()}
                  className={cn(
                    "flex items-center gap-2 cursor-pointer select-none px-3 py-1.5 text-sm text-foreground",
                    "hover:bg-accent focus:bg-accent data-[highlighted]:bg-accent outline-none"
                  )}
                >
                  <span className="flex items-center justify-center w-4 h-4 shrink-0 rounded-sm border border-input">
                    {isChecked && (
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                        <polyline points="4 10 8 15 16 5" />
                      </svg>
                    )}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </DropdownMenu.CheckboxItem>
              );
            })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};


/* ------------------------------------------------------------------ */
/*  Filter-style multi-select (matches RadixSelectControl appearance) */
/* ------------------------------------------------------------------ */

export interface RadixFilterMultiSelectProps {
  options: RadixMultiSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  className?: string;
  placeholder?: string;
  width?: string;
}

export const RadixFilterMultiSelect: React.FC<RadixFilterMultiSelectProps> = ({
  options,
  value,
  onValueChange,
  className,
  placeholder = "Select",
  width,
}) => {
  const selectedSet = new Set(value);

  const displayLabel = React.useMemo(() => {
    if (value.length === 0) return placeholder;
    const firstSelected = options.find((o) => o.value === value[0]);
    const label = firstSelected?.label ?? value[0];
    if (value.length === 1) return label;
    return `${label} +${value.length - 1}`;
  }, [value, options, placeholder]);

  const handleToggle = (optionValue: string) => {
    const next = selectedSet.has(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onValueChange(next);
  };

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={cn(
            "block w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-sm pr-10 flex items-center shadow-sm relative text-left whitespace-nowrap overflow-hidden",
            value.length > 0 ? "text-foreground" : "text-muted-foreground",
            className
          )}
          style={{ height: "32px", minHeight: "32px", maxHeight: "32px", width: width ?? undefined }}
        >
          <span className="truncate">{displayLabel}</span>
          <span
            style={{
              position: "absolute",
              right: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-muted-foreground"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 rounded-md border border-border bg-popover text-popover-foreground shadow-md max-h-64 overflow-auto"
          sideOffset={4}
          align="start"
          style={{ minWidth: "var(--radix-dropdown-menu-trigger-width)" }}
        >
          {options.map((opt) => {
            const isChecked = selectedSet.has(opt.value);
            return (
              <DropdownMenu.CheckboxItem
                key={opt.value}
                checked={isChecked}
                onCheckedChange={() => handleToggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
                className={cn(
                  "flex items-center gap-2 cursor-pointer select-none px-3 py-1.5 text-sm font-normal",
                  "text-foreground",
                  "hover:bg-accent focus:bg-accent data-[highlighted]:bg-accent outline-none"
                )}
              >
                <span className="flex items-center justify-center w-4 h-4 shrink-0 rounded-sm border border-input">
                  {isChecked && (
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                      <polyline points="4 10 8 15 16 5" />
                    </svg>
                  )}
                </span>
                <span className="truncate">{opt.label}</span>
              </DropdownMenu.CheckboxItem>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
